import { ApolloCache } from '@apollo/client'
import { ErrorHandler } from '../../tracing/src'

type EvictOptions = Parameters<ApolloCache<any>['evict']>[0]

/**
 * Evict a field from the Apollo cache based on a predicate function evaluating the field's arguments.
 * Before using this function consider if you can use one of these:
 * - `cache.evict` - If you don't need to filter based on arguments
 * - `cache-and-network` -  fetch policy on query to revalidate the data on revisit
 * - `refetchQueries` - option on mutation
 * - `EventEmitter` - to notify other parts of the app to refresh their data
 *
 * Because the a field can have a version with and without arguments at the same time, when there is no arguments
 * "undefined" will be passed to the predicate. (Example workspace.shares and workspace.shares({ ... })))
 */
export function evictFieldIf<TFieldArguments extends Record<any, any>>(
  cache: ApolloCache<any>,
  { id, fieldName }: { id: EvictOptions['id']; fieldName: string },
  predicate: (variables: TFieldArguments | undefined) => boolean
) {
  cache.modify({
    id: id,
    fields: {
      [fieldName](existing, { storeFieldName, DELETE }) {
        let fieldArguments: TFieldArguments | undefined = undefined

        // Inspired by https://github.com/apollographql/apollo-feature-requests/issues/238#issuecomment-694961960
        if (
          storeFieldName.startsWith(fieldName + '(') ||
          storeFieldName.endsWith(')')
        ) {
          const prefix = `${fieldName}(`
          const suffix = ')'
          const variablesAsString = storeFieldName.slice(
            prefix.length,
            -suffix.length
          )

          try {
            fieldArguments = JSON.parse(
              variablesAsString
            ) as unknown as TFieldArguments
          } catch (e) {
            ErrorHandler.shouldNeverHappen(
              '[evictFieldIf] Could not parse field arguments from storeFieldName: ' +
                storeFieldName
            )
            return existing
          }
        }

        if (predicate(fieldArguments)) {
          return DELETE
        }

        return existing
      },
    },
  })
}
